/**
 * Where to put an Admin group's flyout (W-H6).
 *
 * The flyout used to open at `anchor.right + 4` unconditionally, which left a 3px gap between the
 * Admin menu and the flyout and, for the last groups on a 1528px window, pushed People & Access
 * ~53px past the right edge. Rules now:
 *
 * - Open flush against the anchor (no gap) on the right when the flyout fits there.
 * - Otherwise open to the left of the anchor when it fits there.
 * - If it fits on neither side, use whichever side has more room and clamp it on screen.
 * - Vertically, line the first item up with the anchor and shift up if it would run off the
 *   bottom; never above the top margin.
 */

export interface AnchorRect {
  top: number;
  left: number;
  right: number;
}

export interface FlyoutPlacement {
  top: number;
  left: number;
  side: 'right' | 'left';
}

export interface FlyoutPlacementInput {
  anchor: AnchorRect;
  width: number;
  height: number;
  viewportWidth: number;
  viewportHeight: number;
  /** Keep this much clear of every viewport edge. */
  margin?: number;
  /** The flyout's own top padding, so its first item lines up with the anchor row. */
  offsetTop?: number;
}

export function computeFlyoutPlacement({
  anchor,
  width,
  height,
  viewportWidth,
  viewportHeight,
  margin = 8,
  offsetTop = 0,
}: FlyoutPlacementInput): FlyoutPlacement {
  const roomRight = viewportWidth - margin - anchor.right;
  const roomLeft = anchor.left - margin;

  let side: FlyoutPlacement['side'];
  if (width <= roomRight) side = 'right';
  else if (width <= roomLeft) side = 'left';
  else side = roomRight >= roomLeft ? 'right' : 'left';

  let left = side === 'right' ? anchor.right : anchor.left - width;
  left = Math.max(margin, Math.min(left, viewportWidth - margin - width));

  let top = anchor.top - offsetTop;
  top = Math.min(top, viewportHeight - margin - height);
  top = Math.max(margin, top);

  return { top, left, side };
}
